import React, { useContext, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import { useManualQuery } from 'graphql-hooks'
import orderBy from 'lodash/orderBy'
import { getAddonsSubscription, getLocations } from '../../../../graphql/custom-queries'
import { notificationStore } from '../../../../context/notificationsContext/store'
import * as logger from '../../../../services/logger'
import LinkButton from '../../../../components/link-button'

import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { useEffectivePlan } from '../../../../store/use-effective-plan'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'
import { useAppSelector } from '../../../../store/hooks'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { PageHeader } from '../../../../design-system/PageHeader'
import LocationCard from '../../../../components/location-card'
import { RecordCardList } from '../../../../design-system/RecordCard'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import CircularProgress from '../../../../components/circular-progress'
import { NUMBER_OF_LOCATIONS_LIMIT } from '@vacationtracker/shared/data/app-parameters'

import { IGetLocationsData, IGetLocations } from '../../../../types/locations'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IGetAddonsSubscriptionData } from '../../../../types/custom-queries'
import { AddonTypeEnum } from '@vacationtracker/shared/types/addons'
import CoreCompleteTag from '../../../../components/core-complete-tag'
import FreemiumPricingTooltip from '../../../../components/freemium-pricing-tooltip'
import { useLocationAdmin } from '../../../../hooks/use-location-admin'
import NoPermissionTooltip from '../../../../components/no-permission-tooltip'
import { countHolidaysThisYear, isAutodetectedName } from '../../../../util/location-facts'
import { shouldGroupByCountry } from '@vacationtracker/shared/functions/location-country-groups'
import { CountryGroups } from './CountryGroups'

const SETTLE_INTERVAL_MS = 1500
const SETTLE_ATTEMPTS = 6

const LocationsPage = () => {
  const { actionNotifications } = useContext(notificationStore)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()

  const [isLoading, setIsLoading] = useState(true)
  const [showRenameWarning, setShowRenameWarning] = useState(false)
  const [locationList, setLocationList] = useState<IGetLocations[]>([])
  const [allowUnlimitedLocations, setAllowUnlimitedLocations] = useState(false)

  // A location arrives here before it is finished: LOCATION_CREATED seeds the holidays and leave
  // policies through sibling events, and the websocket drops the correlation id once the first of
  // them lands, so nothing asks again. Until they show up the card would read "no holidays" about
  // a location that is getting some.
  const navState = useLocation<{ settlingLocationId?: string } | undefined>().state
  const [settlingLocationId, setSettlingLocationId] = useState(navState?.settlingLocationId)
  const [settleAttempts, setSettleAttempts] = useState(0)

  const [getLocationsQuery] = useManualQuery<IGetLocationsData>(getLocations)
  const [addonsQuery] = useManualQuery<IGetAddonsSubscriptionData>(getAddonsSubscription)

  useEffect(() => {
    fetchLocationList()
  }, [])

  useEffect(() => {
    fetchLocationList()
  }, [actionNotifications])

  const fetchLocationList = async () => {
    try {
      const response = await getLocationsQuery()
      if (!response.data || response.error) throw response.error

      if (effectivePlan === SubscriptionPlanEnum.core) {
        const addonsResponse = await addonsQuery()
        if (addonsResponse.data?.getAddonsSubscription) {
          setAllowUnlimitedLocations(() => {
            return addonsResponse.data?.getAddonsSubscription?.addons.find((addon) => addon.type === AddonTypeEnum.NO_LIMIT_LOCATION)?.status === 'ACTIVE'
          })
        }
      }

      setLocationList(orderBy(response.data.getLocationList, [(l) => l.isDefault, 'name'], ['desc', 'asc']))
      setIsLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    } catch (err) {
      logger.error('error fetching location list', err)
    }
  }

  useEffect(() => {
    if (!settlingLocationId) return

    // Both seeds, not just the holidays: the backend emits the holiday event well before it has
    // worked out the statutory leave policies, so stopping at the first one leaves the other
    // reporting zero. Either can be zero and correct - a country with no public holidays, a place
    // with no statutory entitlements - and those fall through to the attempt limit below.
    const settling = locationList.find((location) => location.id === settlingLocationId)
    const seeded = settling && countHolidaysThisYear(settling.holidays) > 0 && (settling.leavePolicies?.length ?? 0) > 0
    // Give up after a handful of tries. If the seeding failed, show the zero it really has rather
    // than a spinner that never resolves.
    if (seeded || settleAttempts >= SETTLE_ATTEMPTS) {
      setSettlingLocationId(undefined)
      return
    }

    // Counted after the request settles rather than before it, and in state rather than a ref: a
    // failed refresh leaves the list untouched, so without a dependency that moves either way
    // nothing would re-run this and the card would spin for good.
    const timer = window.setTimeout(async () => {
      await fetchLocationList()
      setSettleAttempts((attempts) => attempts + 1)
    }, SETTLE_INTERVAL_MS)
    return () => window.clearTimeout(timer)
  }, [settlingLocationId, locationList, settleAttempts])

  useEffect(() => {
    const shouldRename = locationList.find((location) => isAutodetectedName(location.name))
    if (shouldRename) {
      setShowRenameWarning(true)
    }
  }, [locationList])

  const shouldDisableCreatingNewLocation = (companyFeatureFlags, companyPlan, locationCount, numberOfLocationsLimit, allowUnlimited) => {
    if (companyFeatureFlags.includes(FeatureFlagEnum.noLimitLocation)) {
      return false
    }

    // Freemium plan is not allowed to create locations
    if (companyPlan === SubscriptionPlanEnum.freemium) return true

    return companyPlan === SubscriptionPlanEnum.core && locationCount >= numberOfLocationsLimit && !allowUnlimited
  }

  const canCreate = !isLocationAdmin && !shouldDisableCreatingNewLocation(featureFlags, effectivePlan, locationList.length, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations)

  const renderCards = (locations: IGetLocations[], firstGroup: boolean) => (
    <RecordCardList>
      {locations.map((location, index) => (
        <LocationCard
          key={location.id}
          id={location.id}
          quickTourLocation={firstGroup && index === 0}
          title={location.name}
          users={location.users}
          isDefault={location.isDefault}
          place={{
            countryCode: location.countryCode ?? '',
            subdivisionCode: location.subdivisionCode ?? null,
          }}
          holidayCount={countHolidaysThisYear(location.holidays)}
          leavePolicyCount={location.leavePolicies?.length ?? 0}
          settling={location.id === settlingLocationId}
          disableEdit={isLocationAdmin && !managedLocationIds.includes(location.id)}
        />
      ))}
    </RecordCardList>
  )

  return (
    <div className="main-content">
      <PageHeader
        title={<IntlMessages id="app.locations" />}
        actions={
          <FreemiumPricingTooltip
            title={<IntlMessages id="components.freemiumPricingTooltip.multipleLocations" />}
            isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium}
            freemiumData={['components.freemiumPricingTooltip.oneLocation', 'components.freemiumPricingTooltip.oneLeaveType']}
            coreData={['components.freemiumPricingTooltip.upTo3Locations', 'components.freemiumPricingTooltip.unlimitedLeaveTypes']}
            completeData={['components.freemiumPricingTooltip.unlimitedLocations', 'components.freemiumPricingTooltip.allCoreFeatures']}
            featureName="create_location"
            featureCategory="locations"
          >
            {isLocationAdmin ? (
              <NoPermissionTooltip>
                <LinkButton type="primary" size="large" to={`${FrontendUrls.locations}/create`} disabled>
                  <IntlMessages id="locations.createNewLocation" />
                </LinkButton>
              </NoPermissionTooltip>
            ) : (
              <LinkButton
                type="primary"
                size="large"
                to={`${FrontendUrls.locations}/create`}
                disabled={shouldDisableCreatingNewLocation(featureFlags, effectivePlan, locationList.length, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations)}
              >
                <IntlMessages id="locations.createNewLocation" />
                {authCompany?.plan === SubscriptionPlanEnum.freemium && <CoreCompleteTag />}
              </LinkButton>
            )}
          </FreemiumPricingTooltip>
        }
      />
      <div className="main-content-body">
        <div className="locations-body">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <div>
              {showRenameWarning && (
                <Alert
                  key="warning"
                  message={<IntlMessages id="locations.pleaseRenameLocationName" />}
                  description={<IntlMessages id="locations.weAutomaticallyCreatedLocations" />}
                  type="warning"
                  showIcon
                  style={{ marginBottom: '20px' }}
                />
              )}
              {shouldGroupByCountry(locationList.map((location) => location.countryCode)) ? (
                <CountryGroups locations={locationList} canCreate={canCreate} renderCards={renderCards} />
              ) : (
                renderCards(locationList, true)
              )}
              {/* freemium plan does not need this warning since it has upsell labels */}
              {[SubscriptionPlanEnum.core].includes(effectivePlan as SubscriptionPlanEnum) &&
                shouldDisableCreatingNewLocation(featureFlags, effectivePlan, locationList.length, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations) && (
                  <Alert
                    style={{ marginTop: spacing.lg, marginBottom: spacing.md }}
                    message={
                      <IntlMessages
                        id="locations.locationLimitReached"
                        values={{
                          limitNumber: NUMBER_OF_LOCATIONS_LIMIT,
                          billingPage: (...chunks) => <Link to="/app/settings/billing">{chunks}</Link>,
                        }}
                      />
                    }
                    type="warning"
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LocationsPage
